/**
 * IconButton sizing tokens — heights match the prototype's `.p-table__kebab`
 * (30px), the topbar `.topbar__icon` (34px) and the larger nav-rail icons
 * (40px). Kept as named sizes (sm/md/tb/lg) so callers don't sprinkle pixels.
 *
 * `tb` ("toolbar") matches the floating canvas toolbar's `.ctb__btn`
 * (32×32 box, 22px glyph) — bigger than the kebab/topbar `md` (30/18) so
 * the icons read clearly at a glance, but smaller than the nav-rail
 * `lg` (40/20) so the toolbar stays a compact pill.
 */
export const ICON_BUTTON_SIZE_XS = 'xs';
export const ICON_BUTTON_SIZE_SM = 'sm';
export const ICON_BUTTON_SIZE_MD = 'md';
export const ICON_BUTTON_SIZE_TB = 'tb';
export const ICON_BUTTON_SIZE_LG = 'lg';

export type IconButtonSize =
  | typeof ICON_BUTTON_SIZE_XS
  | typeof ICON_BUTTON_SIZE_SM
  | typeof ICON_BUTTON_SIZE_MD
  | typeof ICON_BUTTON_SIZE_TB
  | typeof ICON_BUTTON_SIZE_LG;

export const ICON_BUTTON_SIZE_TOKENS: Record<
  IconButtonSize,
  { box: string; iconSize: string }
> = {
  [ICON_BUTTON_SIZE_XS]: { box: '26px', iconSize: '16px' },
  [ICON_BUTTON_SIZE_SM]: { box: '24px', iconSize: '14px' },
  [ICON_BUTTON_SIZE_MD]: { box: '30px', iconSize: '18px' },
  [ICON_BUTTON_SIZE_TB]: { box: '32px', iconSize: '22px' },
  [ICON_BUTTON_SIZE_LG]: { box: '40px', iconSize: '20px' },
};

/**
 * IconButton variants control the resting + hover surface and shape.
 *  - `ghost`: transparent, hover paints surface-container-high. Default for
 *    quiet utility actions (kebabs, dock toggles, the Toast close).
 *  - `tonal`: filled with a tinted surface, used to give the button real
 *    weight (e.g. an FAB-style "expand panel" handle).
 *  - `filled`: high-emphasis primary action — solid `primary` background, used
 *    for the icon-only "+" affordance on list pages.
 *  - `emphasis`: the brand lavender chip (`primaryEmphasis`) worn by the
 *    Process editor's rail "+" button. Same weight as `filled` but with the
 *    emphasis triplet, which holds its contrast in both themes.
 */
export const ICON_BUTTON_VARIANT_GHOST = 'ghost';
export const ICON_BUTTON_VARIANT_TONAL = 'tonal';
export const ICON_BUTTON_VARIANT_FILLED = 'filled';
export const ICON_BUTTON_VARIANT_EMPHASIS = 'emphasis';

export type IconButtonVariant =
  | typeof ICON_BUTTON_VARIANT_GHOST
  | typeof ICON_BUTTON_VARIANT_TONAL
  | typeof ICON_BUTTON_VARIANT_FILLED
  | typeof ICON_BUTTON_VARIANT_EMPHASIS;
